<template>
  <div
    v-if="hasTaxonomy"
    class="product-compatibility"
    :class="{ [$class('active')]: openAccordion }"
  >
    <template v-if="activeVehicle">
      <component
        :is="accordion ? 'button' : 'div'"
        class="product-compatibility__header"
        :class="[
          `bg--${compatibility.textMode}`,
          { 'product-compatibility__header--button': accordion },
        ]"
        :style="styles"
        :type="accordion ? 'button' : null"
        :aria-expanded="accordion ? openAccordion : null"
        @click="accordion ? handleOpenAccordion() : null"
      >
        <component
          :is="compatibility.icon"
          v-if="compatibility.icon"
          aria-hidden="true"
        />

        <div class="product-compatibility__text">
          <span
            class="text-body-xs-regular"
            v-html="compatibility.text.component"
          />

          <btn
            v-if="!accordion"
            class-label="text-body-xs-underline"
            :label="$string('compatibility.see_compatibility')"
            :modes="compatibility.textMode"
            modifiers="text"
            @click="openFinderFlow({ product, type: 'compatibility' })"
          />
        </div>

        <icon-chevron-down
          v-if="accordion"
          aria-hidden="true"
        />
      </component>

      <transition-height v-if="accordion">
        <div v-show="openAccordion">
          <div class="product-compatibility__body">
            <garage-vehicle-details
              class="product-compatibility__vehicle-details"
              template="product"
              :vehicle="activeVehicle"
            />

            <btn
              class="product-compatibility__check"
              class-label="text-body-xs-underline"
              :label="$string('compatibility.check_another_vehicle')"
              modes="light"
              modifiers="text"
              @click="openFinderFlow('garage')"
            />

            <btn
              :label="$string('compatibility.see_compatibility_short')"
              modes="light"
              modifiers="secondary block center"
              @click="openFinderFlow({ product, type: 'compatibility' })"
            />
          </div>
        </div>
      </transition-height>
    </template>

    <div
      v-else
      class="product-compatibility__empty"
    >
      <span
        class="text-body-xs-bold"
        v-text="$string('compatibility.add_your_vehicle')"
      />

      <btn
        class-label="text-body-xs-underline"
        :label="$string('compatibility.check_vehicle')"
        modes="light"
        modifiers="text"
        @click="openFinderFlow('both')"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product compatibility (product-compatibility)
 * -----------------------------------------------------------------------------
 * Displays whether product is compatible with current vehicle and opens modal
 * with compatibility table.
 *
 * @param {Boolean} [accordion] - Display accordion content.
 * @param {Boolean|Object} product - Compatibility product.
 *
 */
import { mapActions, mapGetters } from 'vuex'

import Btn from '~global/btn/btn'
import GarageVehicleDetails from '~async/garage-vehicle-details/garage-vehicle-details'
import TransitionHeight from '~async/transition-height/transition-height'

import IconChevronDown from '~icons/directional-navigation/chevron/down.svg'
import IconCross from '~icons/general/cross.svg'
import IconInfo from '~icons/general/info.svg'
import IconTick from '~icons/general/tick.svg'

import { buildCompatibility } from '~/helpers/garage'

export default {
  name: 'ProductCompatibility',

  components: {
    Btn,
    GarageVehicleDetails,
    IconChevronDown,
    IconCross,
    IconInfo,
    IconTick,
    TransitionHeight,
  },

  props: {
    accordion: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: () => ({
        exclusions: [],
        taxonomy: {},
      }),
      required: true,
    },
  },

  data() {
    return {
      openAccordion: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      activeVehicle: 'garage/getActiveVehicle',
      getCompatibility: 'garage/getCompatibility',
    }),

    /**
     * Compute compatibility.
     * @returns {Object}
     */
    compatibility() {
      const compatibility = this.getCompatibility(this.product?.taxonomy)
      return buildCompatibility(compatibility)
    },

    /**
     * Compute if product has any taxonomy metafields.
     * @returns {Boolean}
     */
    hasTaxonomy() {
      return Object.values(this.product?.taxonomy).filter(Boolean)?.length
    },

    /**
     * Compute styles.
     * @returns {Object}
     */
    styles() {
      if (this.compatibility.type === 'none') {
        return null
      }

      return {
        backgroundColor: `var(${this.compatibility?.colour})`,
      }
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openFinderFlow: 'garage/openFinderFlow',
    }),

    /**
     * Handle opening the accordion.
     */
    handleOpenAccordion() {
      this.openAccordion = !this.openAccordion
    },
  },
}
</script>

<style lang="scss">
@import './product-compatibility';
</style>
